/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { Query } from '@syncfusion/ej2-data';
import { CSS_KEY_BLOCKLIST_RAW } from 'src/components/widgets/graph/settings';
import { StyledDropdownGraphFilterWrapper } from '../styled';

const toStr = (v: any) => v == null ? '' : typeof v === 'string' ? v : Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v);
const getDeep = (obj: any, path: string) => path.split('.').reduce((acc: any, k: string) => (acc != null ? acc[k] : undefined), obj);
const parseTokens = (q: string) => (q.match(/(?:[^\s"]+|"[^"]*")+/g) || []).map((t) => t.replace(/^"|"$/g, ''));
const tokenPredicate = (token: string) => {
  const m = token.match(/^([a-zA-Z0-9_.]+):(.*)$/);
  if (!m) {
    const needle = token.toLowerCase();
    return (data: any) => Object.values(data).some((v) => toStr(v).toLowerCase().includes(needle));
  }
  const [, key, rawVal] = m;
  const val = rawVal.trim().toLowerCase();
  return (data: any) => toStr(getDeep(data, key)).toLowerCase().includes(val);
};

type Mode = 'hide' | 'highlight';

const GraphFilter: React.FC<{ cy: any }> = ({ cy }) => {
  const [filterQuery, setFilterQuery] = useState<string>('');
  const [filterStats, setFilterStats] = useState<{ visible: number; total: number }>({ visible: 0, total: 0 });
  const [mode, setMode] = useState<Mode>('highlight');
  const [availableKeys, setAvailableKeys] = useState<string[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>('name');

  const buildQuery = useCallback((raw: string) => (selectedKey && raw.trim() ? `${selectedKey}:${raw}` : raw), [selectedKey]);

  const refreshKeys = useCallback(() => {
    if (!cy) return;
    const block = new Set((CSS_KEY_BLOCKLIST_RAW || []).map((k) => k.toLowerCase()));
    const ks = new Set<string>();
    cy.nodes(':visible').forEach((n: any) => Object.keys(n.data() || {}).forEach((k) => !block.has(k.toLowerCase()) && ks.add(k)));
    const list = Array.from(ks).sort((a, b) => a.localeCompare(b));
    setAvailableKeys(list);
    if (selectedKey && !list.includes(selectedKey)) setSelectedKey(list.includes('name') ? 'name' : '');
  }, [cy, selectedKey]);

  const applyFilter = useCallback((qRaw: string) => {
    if (!cy) return;
    const q = buildQuery(qRaw);
    const predicates = parseTokens(q).map(tokenPredicate);

    cy.batch(() => {
      cy.elements().removeClass('hidden-by-filter').removeClass('highlight-by-filter').style('display', 'element');

      if (!q.trim()) {
        setFilterStats({ visible: cy.nodes().length, total: cy.nodes().length });
        refreshKeys();
        return;
      }

      let matchCount = 0;
      cy.nodes().forEach((n: any) => {
        const matches = predicates.every((p) => p(n.data()));
        if (matches) {
          matchCount++;
          if (mode === 'highlight') n.addClass('highlight-by-filter');
        } else if (mode === 'hide') {
          n.addClass('hidden-by-filter').style('display', 'none');
        }
      });

      if (mode === 'hide') {
        cy.edges().forEach((e: any) => {
          const sHidden = e.source().hasClass('hidden-by-filter');
          const tHidden = e.target().hasClass('hidden-by-filter');
          if (sHidden || tHidden) e.addClass('hidden-by-filter').style('display', 'none');
        });
      }
      setFilterStats({ visible: matchCount, total: cy.nodes().length });
      refreshKeys();
    });
  }, [cy, mode, buildQuery, refreshKeys]);

  useEffect(() => { const id = setTimeout(() => applyFilter(filterQuery), 150); return () => clearTimeout(id); }, [filterQuery, applyFilter]);
  useEffect(() => { applyFilter(filterQuery); }, [mode]);
  useEffect(() => { applyFilter(filterQuery); }, [selectedKey]);

  useEffect(() => {
    if (!cy) return;
    cy.style()
      .selector('.hidden-by-filter').style({ display: 'none' })
      .selector('.highlight-by-filter').style({ 'overlay-opacity': 0.24, 'overlay-color': '#f1d134', 'border-width': 2, 'border-color': '#f59e0b' })
      .update();

    refreshKeys();
    if (filterQuery) setTimeout(() => applyFilter(filterQuery), 0);
    else setFilterStats({ visible: cy.nodes().length, total: cy.nodes().length });

    const handler = () => refreshKeys();
    cy.on('add remove data', 'node', handler);
    return () => { try { cy.off('add', 'node', handler); cy.off('remove', 'node', handler); cy.off('data', 'node', handler); } catch (_) {} };
  }, [cy]);

  const keyItems = availableKeys.map((k) => ({ text: k, value: k }));

  return (
    <div className="flex items-center gap-1 bg-white/90 border border-gray-300/60 rounded-lg py-0.5 px-1.5 h-8 w-full shadow-sm text-[12px] h-[36px]">
      {/* Key-Dropdown (unstyled) */}
      <StyledDropdownGraphFilterWrapper>
        <DropDownListComponent
          id="graph-key-ddl"
          cssClass="gf-unstyled gf-compact"
          dataSource={keyItems}
          fields={{ text: 'text', value: 'value' }}
          value={selectedKey}
          change={(e: any) => setSelectedKey(e?.value as string)}
          allowFiltering
          ignoreCase
          placeholder="key"
          width="120px"
          filtering={(e: any) => {
            let query = new Query();
            const txt = (e.text || '').toString();
            if (txt) query = query.where('text', 'contains', txt, true);
            e.updateData(keyItems, query);
          }}
          popupHeight="280px"
        />
      </StyledDropdownGraphFilterWrapper>
      <input
        value={filterQuery}
        onChange={(e) => setFilterQuery(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && applyFilter(filterQuery)}
        placeholder={selectedKey ? `${selectedKey}` : 'Filter'}
        className="flex-none w-[72px] sm:w-[96px] md:w-[120px] text-[12px] outline-none bg-transparent"
      />
      {!!filterQuery && (
        <button
          className="px-2 h-7 rounded-md border border-gray-300/60 bg-white hover:bg-gray-50 shadow-sm"
          title="reset filter"
          onClick={() => setFilterQuery('')}
        >
          reset
        </button>
      )}
      <button
        className="px-2 h-7  bg-white hover:bg-gray-50 shadow-sm"
        title={mode === 'hide' ? 'switch to mode highlight' : 'switch to mode hide'}
        onClick={() => setMode(mode === 'hide' ? 'highlight' : 'hide')}
      >
        <span className="text-gray-600">{mode}</span>
      </button>
      <span className="text-[11px] text-gray-500 tabular-nums px-1">{filterStats.visible}/{filterStats.total}</span>
    </div>
  );
};

export default GraphFilter;
